// GiftCardTable.tsx (Corrected)
import { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, CircularProgress, Dialog, DialogTitle, DialogContent, FormControl, InputLabel, Select, MenuItem, DialogActions } from "@mui/material";
import {
  authToken,
  fetchGiftCardStatsAPI,
  GiftCardBase,
  GiftCardListQuery,
  GiftCardTypeExtendDate,
  Product,
  rolesAdmin,
  useSecurityCheck,
  writeOffGiftCard,
} from "../../../utils";
import { AdminGiftCardEntry } from "./AdminGiftCardEntry";
import { CustomSnackbar, Header } from "../../../components";
import { motion } from "framer-motion";
import { useUtility } from "../../../utils/context/UtilityContext";
import {
  fetchGiftCardsAPI,
  createGiftCard,
  updateGiftCard,
} from "../../../utils";
import GiftCardStatistics from "./GiftCardStatistics";
import GroupedGiftCardsTable from "./GroupedGiftCardsTable";
import AllGiftCardsTable from "./AllGiftCardsTable";
import {
  getAllProviders,
  getProductById,
  getProducts,
} from "utils/api/private/ProductApiService";

export const GiftCardTable = () => {
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [allProviders, setAllProviders] = useState<string[]>([]);
  const [allCountries, setAllCountries] = useState<string[]>([]);
  const securityCheck = useSecurityCheck(rolesAdmin);
  const { marginBottomHeader } = useUtility();
  const [productMap, setProductMap] = useState<Record<string, Product>>({});
  const [products, setProducts] = useState<Product[]>([]);
  const [giftCards, setGiftCards] = useState<GiftCardTypeExtendDate[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    totalGiftCards: 0,
    totalSold: 0,
    totalValueSpent: 0,
    revenue: 0,
    potentialRevenue: 0,
    realizedProfit: 0,
    potentialProfit: 0,
  });

  const [writeOffDialogOpen, setWriteOffDialogOpen] = useState(false);
  const [cardToWriteOff, setCardToWriteOff] =
    useState<GiftCardTypeExtendDate | null>(null);
  const [writeOffReason, setWriteOffReason] = useState<
    "expired" | "invalid" | "lost"
  >("invalid");

  useEffect(() => {
    if (!products?.length) return;
    setProductMap((prev) => {
      const next = { ...prev };
      for (const p of products) next[p.id] = p;
      return next;
    });
  }, [products]);

  useEffect(() => {
    const ids = Array.from(new Set(giftCards.map((g) => g.productId))).filter(
      Boolean
    );
    if (ids.length === 0) return;

    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled(
        ids.map((id) => getProductById(id))
      );
      if (cancelled) return;

      const fresh: Record<string, Product> = {};
      results.forEach((r) => {
        if (r.status === "fulfilled" && r.value) {
          fresh[r.value.id] = r.value;
        }
      });

      if (Object.keys(fresh).length) {
        setProductMap((prev) => ({ ...prev, ...fresh }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [giftCards]);

  const [listQuery, setListQuery] = useState<GiftCardListQuery>({
    page: 1,
    limit: 40,
    sortBy: "addedTime",
    order: "desc",
    includeDecrypted: true,
  });

  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentGiftCard, setCurrentGiftCard] = useState<GiftCardBase | null>(
    null
  );

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSuccess, setSnackbarSuccess] = useState(true);

  const gc = Array.isArray(giftCards) ? giftCards : [];

  useEffect(() => {
    (async () => {
      try {
        const s = await fetchGiftCardStatsAPI({ ...listQuery });
        setStats(s);
      } catch (e) {
        console.error("Failed to fetch stats", e);
        setStats({
          totalGiftCards: 0,
          totalSold: 0,
          totalValueSpent: 0,
          revenue: 0,
          potentialRevenue: 0,
          realizedProfit: 0,
          potentialProfit: 0,
        });
      }
    })();
  }, [
    listQuery,
    listQuery.search,
    listQuery.status,
    listQuery.category,
    listQuery.provider,
    listQuery.country,
  ]);

  // This useEffect now ONLY runs once on mount to get filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const providersData = await getAllProviders();
        const providerNames = providersData.map((p) => p.name).sort();
        setAllProviders(providerNames);

        const productsResponse = await getProducts({ page: 1, limit: 10000 });
        const allProds = productsResponse.data;
        setProducts(allProds);

        const categories = Array.from(
          new Set(allProds.map((p) => p.category).filter(Boolean))
        ).sort();
        const countries = Array.from(
          new Set(allProds.map((p) => p.countryCode).filter(Boolean))
        ).sort();

        setAllCategories(categories);
        setAllCountries(countries);
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
      }
    };

    fetchFilterOptions();
  }, []);

  // This is the ONLY function that should fetch the main list data
  const fetchGiftCards = useCallback(async () => {
    setLoading(true);
    try {
      const { data, meta } = await fetchGiftCardsAPI(listQuery);
      setGiftCards(Array.isArray(data) ? data : []);
      setTotalCount(typeof meta?.total === "number" ? meta.total : 0);
    } catch (e: any) {
      setGiftCards([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [listQuery]);

  useEffect(() => {
    fetchGiftCards();
  }, [fetchGiftCards]);

  const groupGiftCards = (giftCardsList: GiftCardTypeExtendDate[]) => {
    const list = Array.isArray(giftCardsList) ? giftCardsList : [];
    const grouped = list.reduce<Record<string, any>>((acc, card) => {
      const key = card.productId;
      acc[key] ??= {
        ...card,
        quantity: 0,
        quantitySold: 0,
        quantityNotSold: 0,
      };
      acc[key].quantity += 1;

      // CHANGED: Use card.status instead of card.sold
      if (card.status === "sold") {
        acc[key].quantitySold += 1;
      } else {
        acc[key].quantityNotSold += 1;
      }
      return acc;
    }, {});
    return Object.values(grouped);
  };

  const handleAdd = () => {
    setCurrentGiftCard(null);
    setOpenDialog(true);
  };

  const handleSave = async (giftCard: GiftCardBase) => {
    setLoading(true);
    try {
      if (giftCard.id) {
        await updateGiftCard(giftCard);
        setSnackbarMessage("Gift card updated successfully");
      } else {
        await createGiftCard(giftCard);
        setSnackbarMessage("Gift card created successfully");
      }
      setSnackbarSuccess(true);
      await fetchGiftCards();
      setOpenDialog(false);
    } catch (error: any) {
      setSnackbarMessage(error?.message || "Failed to save gift card");
      setSnackbarSuccess(false);
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleOpenWriteOffDialog = (giftCard: GiftCardTypeExtendDate) => {
    setCardToWriteOff(giftCard);
    setWriteOffDialogOpen(true);
  };

  const handleCloseWriteOffDialog = () => {
    setCardToWriteOff(null);
    setWriteOffDialogOpen(false);
  };

  const handleConfirmWriteOff = async () => {
    if (!cardToWriteOff) return;
    const token = localStorage.getItem(authToken);
    if (!token) {
      setSnackbarMessage("Authentication failed");
      setSnackbarSuccess(false);
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      await writeOffGiftCard(cardToWriteOff.id, writeOffReason, token);
      setSnackbarMessage("Card successfully written off.");
      setSnackbarSuccess(true);
      await fetchGiftCards(); // Refresh the list
    } catch (error: any) {
      setSnackbarMessage(error.message || "Failed to write off card.");
      setSnackbarSuccess(false);
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
      handleCloseWriteOffDialog();
    }
  };

  if (securityCheck) return securityCheck;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`${marginBottomHeader} flex flex-col items-center justify-start gap-4 overflow-x-hidden fancy-scroll w-full`}
    >
      <Header />
      <Box className="w-5/6 p-8">
        <Typography variant="h4">Gift Card Inventory</Typography>
        <Button
          variant="contained"
          sx={{ backgroundColor: "#133B62" }}
          onClick={handleAdd}
        >
          Ajouter un nouveau code
        </Button>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <GroupedGiftCardsTable
              groupedGiftCards={groupGiftCards(gc)}
              products={products}
            />

            <AllGiftCardsTable
              giftCards={gc}
              totalCount={totalCount}
              allCategories={allCategories}
              onWriteOff={handleOpenWriteOffDialog}
              allProviders={allProviders}
              allCountries={allCountries}
              productMap={productMap}
              refreshGiftCards={fetchGiftCards}
              setCurrentGiftCard={setCurrentGiftCard}
              setOpenDialog={setOpenDialog}
              page={listQuery.page ?? 1}
              limit={listQuery.limit ?? 40}
              order={listQuery.order ?? "desc"}
              orderBy={
                (listQuery.sortBy ?? "addedTime") as NonNullable<
                  GiftCardListQuery["sortBy"]
                >
              }
              filters={{
                category: listQuery.category || "",
                provider: listQuery.provider || "",
                country: listQuery.country || "",
              }}
              search={listQuery.search || ""}
              onChangeQuery={(partial) =>
                setListQuery((q) => ({ ...q, ...partial, page: 1 }))
              }
              onChangePage={(page) => setListQuery((q) => ({ ...q, page }))}
              onChangeLimit={(limit) =>
                setListQuery((q) => ({ ...q, limit, page: 1 }))
              }
              onChangeSort={(sortBy, order) =>
                setListQuery((q) => ({ ...q, sortBy, order }))
              }
            />

            <GiftCardStatistics s={stats} />
          </>
        )}

        <AdminGiftCardEntry
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSave={handleSave}
          currentGiftCard={currentGiftCard || undefined}
        />

        <Dialog open={writeOffDialogOpen} onClose={handleCloseWriteOffDialog}>
          <DialogTitle>Write Off Gift Card</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to write off the card "
              {cardToWriteOff?.fullname}"? This action will mark it as a loss
              and cannot be undone.
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Reason</InputLabel>
              <Select
                value={writeOffReason}
                label="Reason"
                onChange={(e) => setWriteOffReason(e.target.value as any)}
              >
                <MenuItem value="invalid">Invalid</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="lost">Lost</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseWriteOffDialog}>Cancel</Button>
            <Button onClick={handleConfirmWriteOff} color="error">
              Confirm Write-Off
            </Button>
          </DialogActions>
        </Dialog>

        <CustomSnackbar
          open={snackbarOpen}
          message={snackbarMessage}
          isSuccess={snackbarSuccess}
          handleClose={handleSnackbarClose}
        />
      </Box>
    </motion.div>
  );
};
